import { useEffect, useRef, useState } from 'react';

type AutoComplete = google.maps.places.Autocomplete;
type MapsEventListener = google.maps.MapsEventListener;

export interface SelectedPlace {
  name: string;
  lat: number;
  lng: number;
  countryCode: string | null;
}

export const useAutocomplete = () => {
  const [currentSearchValue, setCurrentSearchValue] = useState<string>('');
  const locationRef = useRef<HTMLInputElement>(null);
  const [currentPlaceListenerId, setCurrentPlaceListenerId] = useState<MapsEventListener | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<SelectedPlace | null>(null);

  useEffect(() => {
    if (locationRef.current) {
      generateAutoComplete();
    }

    return () => {
      if (currentPlaceListenerId) {
        google.maps.event.removeListener(currentPlaceListenerId);
      }
    };
  }, [locationRef.current]);

  const autoCompleteHandler = (autocomplete: AutoComplete): any => {
    let listener = autocomplete?.addListener('place_changed', function () {
      const place = autocomplete!.getPlace();
      if (!place.geometry || !place.geometry.location || !place.formatted_address) return;
      const placeName = place.formatted_address.substring(0, place.formatted_address.indexOf(','));
      let countryCode;

      if (place.address_components) {
        countryCode = place.address_components.find((el) => el.types.includes('country'))?.short_name;
      }

      setSelectedPlace({
        name: placeName,
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
        countryCode: countryCode ?? '',
      });
      setCurrentSearchValue('');
    });
    setCurrentPlaceListenerId(listener);
  };

  const generateAutoComplete = () => {
    if (currentPlaceListenerId) {
      google.maps.event.removeListener(currentPlaceListenerId);
    }

    setCurrentPlaceListenerId(null);
    const searchInputField = document.getElementById(`autocomplete-1`);
    const autocomplete = new window.google.maps.places.Autocomplete(searchInputField as HTMLInputElement, {
      fields: ['geometry', 'formatted_address', 'name', 'address_component', 'place_id'],
    });
    autoCompleteHandler(autocomplete);
  };

  return { locationRef, currentSearchValue, setCurrentSearchValue, selectedPlace };
};
